import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Send, Copy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const InterpellationDialog = ({ open, onOpenChange, defaultName }) => {
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [name, setName] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(defaultName || '');
    Promise.all([
      axios.get(`${API}/candidates`),
      axios.get(`${API}/settings/interpellation_subject`),
      axios.get(`${API}/settings/interpellation_body`),
    ]).then(([c, s, b]) => {
      setCandidates(c.data);
      setSelected(c.data.map((x) => x.id));
      setSubject(s.data.value);
      setBody(b.data.value);
    }).catch(() => toast.error('Impossible de charger les candidats'));
  }, [open, defaultName]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const emails = candidates.filter((c) => selected.includes(c.id)).map((c) => c.email);
  const fullBody = name.trim() ? `${body}\n\n${name.trim()}` : body;

  const send = () => {
    if (emails.length === 0) return toast.error('Sélectionnez au moins un candidat');
    window.location.href = `mailto:${emails.join(',')}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(fullBody)}`;
  };

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(`À : ${emails.join(', ')}\nObjet : ${subject}\n\n${fullBody}`);
      toast.success('Destinataires et message copiés');
    } catch { toast.error('Copie impossible'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="interpellation-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Interpeller les candidats</DialogTitle>
          <DialogDescription>Choisissez les candidats, relisez le message, puis envoyez-le depuis votre messagerie.</DialogDescription>
        </DialogHeader>

        {candidates.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4" data-testid="interpellation-no-candidates">La liste des candidats sera bientôt disponible.</p>
        ) : (
          <div className="space-y-5">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Candidats ({selected.length}/{candidates.length})</Label>
                <button type="button" className="text-xs text-primary hover:underline" data-testid="interpellation-toggle-all"
                  onClick={() => setSelected(selected.length === candidates.length ? [] : candidates.map((c) => c.id))}>
                  {selected.length === candidates.length ? 'Tout décocher' : 'Tout cocher'}
                </button>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                {candidates.map((c) => (
                  <label key={c.id} className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer" data-testid={`interpellation-candidate-${c.id}`}>
                    <Checkbox checked={selected.includes(c.id)} onCheckedChange={() => toggle(c.id)} />
                    <span className="text-sm"><span className="font-medium">{c.name}</span>{c.party && <span className="block text-xs text-muted-foreground">{c.party}</span>}</span>
                  </label>
                ))}
              </div>
            </div>
            <div><Label>Votre nom (signature)</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" data-testid="interpellation-name-input" /></div>
            <div><Label>Objet</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1" data-testid="interpellation-subject" /></div>
            <div><Label>Message</Label><Textarea rows={10} value={body} onChange={(e) => setBody(e.target.value)} className="mt-1 text-sm" data-testid="interpellation-body" /></div>
            <div className="flex flex-wrap gap-3 justify-end">
              <Button variant="outline" onClick={copyAll} className="gap-2" data-testid="interpellation-copy-btn"><Copy className="w-4 h-4" />Copier</Button>
              <Button onClick={send} className="gap-2" data-testid="interpellation-send-btn"><Send className="w-4 h-4" />Ouvrir ma messagerie</Button>
            </div>
            <p className="text-xs text-muted-foreground">Si votre messagerie ne s'ouvre pas, utilisez « Copier » puis collez dans votre email.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
